import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback } from "react";
import {
  toggleFavorite as toggleFavoriteThunk,
  getFavorites as getFavoritesThunk,
  getFavoriteIds as getFavoriteIdsThunk,
  getFavoriteTabs as getFavoriteTabsThunk,
  bulkDeleteFavorites as bulkDeleteFavoritesThunk,
} from "./favorite.thunk";
import {
  setFavoritedProducts,
  addFavoritedProduct,
  removeFavoritedProduct,
  clearFavorites,
  initializeFavoriteState,
} from "./favorite.slice";
import { isLoading, isSuccess, isError } from "@/app/utils/asyncStatus";
import { GetFavoritesRequest } from "./favorite.types";

/**
 * useFavorite tüm `state.favorite` slice'ına abone. FavoriteButton kendi
 * durumunu local state'te tutuyor (kaynak: variants_by_color[].is_favorited),
 * slice'tan hiçbir şey okumuyor — sadece toggle ediyor. Sayfadaki her kartta bir
 * FavoriteButton var; bu dispatch-only sürüm 84 gereksiz aboneliği kaldırıyor.
 */
export const useToggleFavorite = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    async (productId: number, variantId?: number) => {
      const result = await dispatch(
        toggleFavoriteThunk({ productId, variantId }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );
};

const useFavorite = () => {
  const dispatch = useAppDispatch();
  const {
    data: favorites,
    favoritedProducts,
    togglingProducts,
    tabs,
    status,
    tabsStatus,
    idsStatus,
    bulkDeleteStatus,
    error,
  } = useAppSelector((store) => store.favorite);

  /**
   * Toggle favorite status for a product
   */
  const toggleFavorite = useCallback(
    async (productId: number, variantId?: number) => {
      const result = await dispatch(toggleFavoriteThunk({ productId, variantId }));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Get favorite products list with pagination and filters
   */
  const fetchFavorites = useCallback(
    async (params?: GetFavoritesRequest) => {
      const result = await dispatch(getFavoritesThunk(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Load more favorites (infinite scroll)
   */
  const fetchMore = useCallback(() => {
    if (!favorites?.pagination) return;
    
    const { current_page, last_page, per_page } = favorites.pagination;
    if (current_page >= last_page) return;

    dispatch(getFavoritesThunk({
      page: current_page + 1,
      per_page,
    }));
  }, [dispatch, favorites]);

  /**
   * Get only favorite product IDs
   */
  const fetchFavoriteIds = useCallback(async () => {
    const result = await dispatch(getFavoriteIdsThunk());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Get available tabs for favorites
   */
  const fetchFavoriteTabs = useCallback(async () => {
    const result = await dispatch(getFavoriteTabsThunk());
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /**
   * Bulk delete favorites
   */
  const bulkDelete = useCallback(
    async (productIds: number[]) => {
      const result = await dispatch(
        bulkDeleteFavoritesThunk({ product_ids: productIds }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /**
   * Check if a product is favorited
   */
  const isFavorited = useCallback(
    (productId: number) => {
      return favoritedProducts.includes(productId);
    },
    [favoritedProducts],
  );

  /**
   * Check if a product is currently being toggled
   */
  const isToggling = useCallback(
    (productId: number) => {
      return togglingProducts.includes(productId);
    },
    [togglingProducts],
  );

  /**
   * Initialize favorited products (e.g., from API or product list)
   */
  const initializeFavorites = useCallback(
    (productIds: number[]) => {
      dispatch(setFavoritedProducts(productIds));
    },
    [dispatch],
  );

  /**
   * Initialize single product favorite state (from API)
   */
  const initializeProductFavorite = useCallback(
    (productId: number, isFavorited: boolean) => {
      dispatch(initializeFavoriteState({ productId, isFavorited }));
    },
    [dispatch],
  );

  /**
   * Manually add a product to favorites (optimistic update)
   */
  const addToFavorites = useCallback(
    (productId: number) => {
      dispatch(addFavoritedProduct(productId));
    },
    [dispatch],
  );

  /**
   * Manually remove a product from favorites (optimistic update)
   */
  const removeFromFavorites = useCallback(
    (productId: number) => {
      dispatch(removeFavoritedProduct(productId));
    },
    [dispatch],
  );

  /**
   * Clear all favorites
   */
  const clearAllFavorites = useCallback(() => {
    dispatch(clearFavorites());
  }, [dispatch]);

  return {
    // Data
    favorites,
    favoritedProducts,
    togglingProducts,
    tabs,
    error,

    // Status
    status,
    tabsStatus,
    idsStatus,
    bulkDeleteStatus,

    // Status helpers
    isLoading: isLoading(status),
    isSuccess: isSuccess(status),
    isError: isError(status),
    isTabsLoading: isLoading(tabsStatus),
    isIdsLoading: isLoading(idsStatus),
    isBulkDeleting: isLoading(bulkDeleteStatus),

    // Methods
    toggleFavorite,
    fetchFavorites,
    fetchMore,
    fetchFavoriteIds,
    fetchFavoriteTabs,
    bulkDelete,
    isFavorited,
    isToggling,
    initializeFavorites,
    initializeProductFavorite,
    addToFavorites,
    removeFromFavorites,
    clearAllFavorites,
  };
};

export default useFavorite;